import { useMaximize } from "./hooks/useMaximize";
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import "./App.css";

export default function Warehouse({ onClose, user }) {
  const [tab, setTab] = useState("receive");
  const { toggle, overlayClass, panelClass, icon } = useMaximize(true);

  return (
    <div className={overlayClass}>
      <div className={panelClass} style={{ maxWidth: "980px" }}>
        <div className="report-header">
          <div>
            <h2 className="report-title">🏪 Warehouse Manager</h2>
            <p className="report-date">
              Stock flow: Supplier → Warehouse → Shelf
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div className="inv-tabs">
              {[
                { key: "receive", label: "📥 Receive Stock" },
                { key: "suppliers", label: "🏢 Suppliers" },
                { key: "history", label: "📋 History" },
              ].map((t) => (
                <button
                  key={t.key}
                  className={`inv-tab ${tab === t.key ? "active" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {/* Maximize button */}
            <button
              className="maximize-btn"
              onClick={toggle}
              title={icon === "⊞" ? "Maximize" : "Restore"}
            >
              {icon}
            </button>
          </div>
          <button className="report-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {tab === "receive" && <ReceiveStock user={user} />}
        {tab === "suppliers" && <Suppliers user={user} />}
        {tab === "history" && <History />}
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// TAB 1 — Receive Stock
// ══════════════════════════════════════
function ReceiveStock({ user }) {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [notes, setNotes] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [poStatus, setPoStatus] = useState("completed");
  const [items, setItems] = useState([
    {
      product_id: "",
      product_name: "",
      quantity: "",
      unit_cost: "",
      uom: "",
      expiry_date: "",
      batch_number: "",
    },
  ]);
  const [saving, setSaving] = useState(false);

  function generatePO() {
    const d = new Date();
    const ds = d.toISOString().slice(0, 10).replace(/-/g, "");
    return `PO-${ds}-${Math.floor(Math.random() * 9000) + 1000}`;
  }

  useEffect(() => {
    setPoNumber(generatePO());
    supabase
      .from("suppliers")
      .select("id, name, tin, phone, email, address, website")
      .order("name")
      .then(({ data, error }) => {
        logSupabaseError("Warehouse: ReceiveStock load suppliers", error);
        if (data) setSuppliers(data);
      });
    supabase
      .from("products")
      .select(
        "id, name, barcode, sku_code, sale_unit, purchase_unit, cost_price, units_per_purchase",
      )
      .eq("is_active", true)
      .order("name")
      .then(({ data, error }) => {
        logSupabaseError("Warehouse: ReceiveStock load products", error);
        if (data) setProducts(data);
      });
  }, []);

  function addRow() {
    setItems((prev) => [
      ...prev,
      {
        product_id: "",
        product_name: "",
        quantity: "",
        unit_cost: "",
        uom: "",
        expiry_date: "",
        batch_number: "",
      },
    ]);
  }

  function removeRow(i) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateRow(i, field, value) {
    setItems((prev) =>
      prev.map((item, idx) => (idx === i ? { ...item, [field]: value } : item)),
    );
  }

  function selectProduct(i, product) {
    setItems((prev) =>
      prev.map((item, idx) =>
        idx === i
          ? {
              ...item,
              product_id: product.id,
              product_name: product.name,
              uom: product.purchase_unit || product.sale_unit || "piece",
              unit_cost:
                product.cost_price > 0 ? product.cost_price : item.unit_cost,
            }
          : item,
      ),
    );
  }

  // Unit Cost / Purchase is entered per purchase unit (e.g. "500 ETB per
  // case of 24"), matching what a manager's supplier invoice actually says
  // — so the money total is simply qty(purchase units) × unit_cost, with no
  // units_per_purchase multiplication. That conversion only matters when
  // deriving a per-sale-unit cost_price for margin comparisons, done in
  // handleReceive where the product's units_per_purchase is read.
  const totalCost = items.reduce(
    (sum, i) => sum + (Number(i.quantity) * Number(i.unit_cost) || 0),
    0,
  );

  async function handleReceive() {
    if (!supplier) {
      alert("Please select a supplier!");
      return;
    }
    const validItems = items.filter(
      (i) => i.product_id && Number(i.quantity) > 0,
    );

    if (
      validItems.length === 0 &&
      poStatus !== "pending" &&
      poStatus !== "cancelled"
    ) {
      alert("Please add at least one product with quantity!");
      return;
    }

    setSaving(true);

    // Every lookup the receive depends on runs in Phase 1, before anything
    // is written — so aborting on a failed lookup really does mean nothing
    // was saved, and the manager can safely retry. Never fall back to a
    // default on a failed read: those values feed the writes in Phase 2
    // (e.g. a defaulted-to-0 warehouse qty would overwrite existing stock).
    function abortReceive(what, error) {
      logSupabaseError(`Warehouse: ReceiveStock ${what}`, error);
      alert(
        `❌ Receive failed — couldn't ${what}.\n\nNothing was saved. Please check your connection and try again.` +
          (error?.message ? `\n\nDetails: ${error.message}` : ""),
      );
      setSaving(false);
    }

    // ── Phase 1: lookups (no writes) ──
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("store_id")
      .eq("id", user.id)
      .single();
    if (profileError || !profileData?.store_id) {
      abortReceive("load your store profile", profileError);
      return;
    }
    const storeId = profileData.store_id;

    // Running per-product and per-batch state: seeded from the DB here, then
    // advanced in memory as each line is written in Phase 2, so the same
    // product on two lines (e.g. two expiry dates) sees the earlier line's
    // stock/cost/batch changes — as it did when each line re-read the DB.
    const productState = {};
    const batchState = {};
    const stockLines = [];
    if (poStatus === "completed" || poStatus === "partial") {
      for (const item of validItems) {
        if (!productState[item.product_id]) {
          const { data: prod, error: prodError } = await supabase
            .from("products")
            .select("cost_price, units_per_purchase")
            .eq("id", item.product_id)
            .single();
          if (prodError) {
            abortReceive(`load product details for "${item.product_name}"`, prodError);
            return;
          }

          // No row (null data, no error) is a legitimate "no stock yet" and
          // starts from 0 — only an actual error aborts.
          const { data: ws, error: wsLookupError } = await supabase
            .from("warehouse_stock")
            .select("quantity")
            .eq("product_id", item.product_id)
            .maybeSingle();
          if (wsLookupError) {
            abortReceive(`read current warehouse stock for "${item.product_name}"`, wsLookupError);
            return;
          }

          productState[item.product_id] = {
            costPrice: Number(prod.cost_price ?? 0),
            // item.unit_cost is per purchase unit (what the manager's invoice
            // says, e.g. per case) — cost_price must be per SALE unit to be
            // comparable to selling_price for margin calculations, so it's
            // divided by units_per_purchase in Phase 2.
            unitsPerPurchase: Math.max(1, Number(prod.units_per_purchase ?? 1)),
            warehouseQty: Number(ws?.quantity ?? 0),
          };
        }

        const batchNumber =
          (item.batch_number || "").trim() || `B-${Date.now()}`;
        const expiryDate = item.expiry_date || null;
        const batchKey = `${item.product_id}|${batchNumber}|${expiryDate ?? ""}`;
        if (!(batchKey in batchState)) {
          const batchQuery = supabase.from("product_batches").select("id, quantity");
          const { data: existingBatch, error: existingBatchError } = expiryDate
            ? await batchQuery
                .match({
                  product_id: item.product_id,
                  batch_number: batchNumber,
                  expiry_date: expiryDate,
                })
                .maybeSingle()
            : await batchQuery
                .match({ product_id: item.product_id, batch_number: batchNumber })
                .is("expiry_date", null)
                .maybeSingle();
          if (existingBatchError) {
            abortReceive(`check existing batch records for "${item.product_name}"`, existingBatchError);
            return;
          }
          batchState[batchKey] = existingBatch
            ? { id: existingBatch.id, quantity: Number(existingBatch.quantity ?? 0) }
            : null;
        }

        stockLines.push({ item, batchNumber, expiryDate, batchKey });
      }
    }

    // ── Phase 2: writes ──
    // Save PO record always. PO Number is user-editable (managers can type
    // a number to match a supplier's own paper PO/invoice), so it isn't
    // generated server-side — the DB enforces uniqueness via a constraint,
    // and on a collision we retry once with a freshly generated number.
    function insertOrder(poNum) {
      return supabase
        .from("purchase_orders")
        .insert({
          supplier_id: supplier,
          received_by: user.id,
          total_cost: ["pending", "cancelled"].includes(poStatus)
            ? 0
            : totalCost,
          notes: notes || null,
          status: poStatus,
          po_number: poNum || null,
          store_id: storeId,
        })
        .select()
        .single();
    }

    let currentPoNumber = poNumber;
    let { data: order, error: orderError } = await insertOrder(currentPoNumber);

    if (orderError?.code === "23505") {
      currentPoNumber = generatePO();
      setPoNumber(currentPoNumber);
      ({ data: order, error: orderError } = await insertOrder(currentPoNumber));
    }

    if (orderError) {
      alert(
        orderError.code === "23505"
          ? "That PO Number is already in use — please change it and save again."
          : "Error saving PO: " + orderError.message,
      );
      setSaving(false);
      return;
    }

    // Save line items for all statuses (for records)
    if (validItems.length > 0) {
      const { error: itemsError } = await supabase.from("purchase_order_items").insert(
        validItems.map((i) => ({
          order_id: order.id,
          product_id: i.product_id,
          quantity: Number(i.quantity),
          unit_cost: Number(i.unit_cost),
          line_total: Number(i.quantity) * Number(i.unit_cost),
          expiry_date: i.expiry_date || null,
          batch_number: i.batch_number || null,
          store_id: storeId,
        })),
      );
      if (itemsError) {
        alert("❌ Failed to save order line items: " + itemsError.message);
        setSaving(false);
        return;
      }
    }

    // Only update warehouse stock for completed or partial (stockLines is
    // empty for pending/cancelled)
    for (const { item, batchNumber, expiryDate, batchKey } of stockLines) {
      const product = productState[item.product_id];
      const oldCost = product.costPrice;
      const newCost = Number(item.unit_cost) / product.unitsPerPurchase;
      const receivedQty =
        Math.max(0, Number(item.quantity) || 0) * product.unitsPerPurchase;
      const newWarehouseQty = product.warehouseQty + receivedQty;

      const { error: wsError } = await supabase.from("warehouse_stock").upsert(
        {
          product_id: item.product_id,
          quantity: newWarehouseQty,
          updated_at: new Date().toISOString(),
          store_id: storeId,
        },
        { onConflict: "product_id" },
      );
      if (wsError) {
        alert("❌ Failed to update warehouse stock for \"" + item.product_name + "\": " + wsError.message);
        setSaving(false);
        return;
      }
      product.warehouseQty = newWarehouseQty;

      // Update cost price to latest received batch purchase
      if (newCost > 0) {
        const { error: costError } = await supabase
          .from("products")
          .update({ cost_price: newCost })
          .eq("id", item.product_id);
        if (costError) {
          alert("❌ Failed to update cost price for \"" + item.product_name + "\": " + costError.message);
          setSaving(false);
          return;
        }
        product.costPrice = newCost;

        if (oldCost !== newCost) {
          const { error: priceHistError } = await supabase.from("price_history").insert({
            product_id: item.product_id,
            price_type: "cost",
            old_price: oldCost > 0 ? oldCost : null,
            new_price: newCost,
            changed_by: user.id,
            reason: `PO: ${currentPoNumber}`,
            store_id: storeId,
          });
          if (priceHistError) {
            alert("❌ Failed to log price history for \"" + item.product_name + "\": " + priceHistError.message);
            setSaving(false);
            return;
          }
        }
      }

      // Batch record per PO item (supports future FEFO/FIFO).
      // - `quantity` is stored in sale units (purchase qty × units_per_purchase)
      // - `cost_price` stores the batch unit cost per SALE unit, matching
      //   `products.cost_price` above (both derived from the entered
      //   per-purchase-unit unit_cost ÷ units_per_purchase)
      // - `received_date` is set on insert only so FIFO ordering stays stable
      const batch = batchState[batchKey];
      if (batch) {
        const { error: batchUpdateError } = await supabase
          .from("product_batches")
          .update({
            batch_number: batchNumber,
            expiry_date: expiryDate,
            quantity: batch.quantity + receivedQty,
            cost_price: newCost > 0 ? newCost : null,
          })
          .eq("id", batch.id);
        if (batchUpdateError) {
          alert("❌ Failed to update batch record for \"" + item.product_name + "\": " + batchUpdateError.message);
          setSaving(false);
          return;
        }
        batch.quantity += receivedQty;
      } else {
        const { data: newBatch, error: batchInsertError } = await supabase
          .from("product_batches")
          .insert({
            product_id: item.product_id,
            batch_number: batchNumber,
            expiry_date: expiryDate,
            quantity: receivedQty,
            cost_price: newCost > 0 ? newCost : null,
            received_date: new Date().toISOString().split("T")[0],
            store_id: storeId,
          })
          .select("id")
          .single();
        if (batchInsertError) {
          alert("❌ Failed to save batch record for \"" + item.product_name + "\": " + batchInsertError.message);
          setSaving(false);
          return;
        }
        // A later line with the same product/batch/expiry updates this row
        // instead of inserting a duplicate.
        batchState[batchKey] = { id: newBatch.id, quantity: receivedQty };
      }
    }

    // Show appropriate message
    const messages = {
      completed: `✅ Stock received into warehouse!\nPO: ${currentPoNumber}\nTotal: ETB ${totalCost.toFixed(2)}`,
      partial: `📦 Partial delivery saved!\nPO: ${currentPoNumber}\nItems added to warehouse.`,
      pending: `⏳ PO saved as Pending!\nPO: ${currentPoNumber}\nNo stock added yet.`,
      cancelled: `❌ PO saved as Cancelled.\nPO: ${currentPoNumber}\nNo stock was added.`,
    };
    alert(messages[poStatus] ?? "Saved!");

    // Reset form
    setItems([
      {
        product_id: "",
        product_name: "",
        quantity: "",
        unit_cost: "",
        uom: "",
        expiry_date: "",
        batch_number: "",
      },
    ]);
    setSupplier("");
    setSupplierInfo(null);
    setNotes("");
    setPoNumber(generatePO());
    setSaving(false);
  }

  return (
    <div
      style={{
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        {/* ── Order Details ── */}
        <div className="wh-section">
          <p className="wh-section-title">📋 Order Details</p>
          <div
            className="wh-order-details-row"
            style={{ marginBottom: "12px" }}
          >
            {/* Supplier */}
            <div className="inv-form-field">
              <label>Supplier *</label>
              <select
                className="inv-select"
                style={{ width: "100%" }}
                value={supplier}
                onChange={(e) => {
                  setSupplier(e.target.value);
                  setSupplierInfo(
                    suppliers.find((s) => s.id === e.target.value) ?? null,
                  );
                }}
              >
                <option value="">Select supplier...</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* PO Status */}
            <div className="inv-form-field">
              <label>PO Status</label>
              <select
                className="inv-select"
                style={{ width: "100%" }}
                value={poStatus}
                onChange={(e) => setPoStatus(e.target.value)}
              >
                <option value="completed">✅ Completed</option>
                <option value="partial">📦 Partially Received</option>
                <option value="pending">⏳ Pending</option>
                <option value="cancelled">❌ Cancelled</option>
              </select>
            </div>

            {/* PO Number */}
            <div className="inv-form-field">
              <label>PO Number</label>
              <div style={{ display: "flex", gap: "6px" }}>
                <input
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  style={{
                    flex: 1,
                    height: "36px",
                    padding: "0 10px",
                    background: "var(--elevated)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius)",
                    color: "var(--cyan)",
                    fontFamily: "var(--mono)",
                    outline: "none",
                    fontSize: "13px",
                  }}
                />
                <button
                  className="wh-add-item-btn"
                  onClick={() => setPoNumber(generatePO())}
                >
                  ↻
                </button>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="inv-form-field">
            <label>Notes (optional)</label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Delivery note reference, special instructions..."
              style={{
                width: "100%",
                height: "36px",
                padding: "0 12px",
                background: "var(--elevated)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                color: "var(--text)",
                fontFamily: "var(--font)",
                outline: "none",
                fontSize: "13px",
              }}
            />
          </div>

          {/* Supplier Info Card */}
          {supplierInfo && (
            <div className="supplier-info-card" style={{ marginTop: "12px" }}>
              <div className="supplier-info-header">
                <div className="supplier-info-avatar">
                  {supplierInfo.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="supplier-info-name">{supplierInfo.name}</p>
                  <p className="supplier-info-sub">Supplier Profile</p>
                </div>
              </div>
              <div className="supplier-info-grid">
                {supplierInfo.tin && (
                  <div className="supplier-info-item">
                    <span className="supplier-info-label">🏛️ TIN</span>
                    <span className="supplier-info-value">
                      {supplierInfo.tin}
                    </span>
                  </div>
                )}
                {supplierInfo.phone && (
                  <div className="supplier-info-item">
                    <span className="supplier-info-label">📞 Phone</span>
                    <span className="supplier-info-value">
                      {supplierInfo.phone}
                    </span>
                  </div>
                )}
                {supplierInfo.email && (
                  <div className="supplier-info-item">
                    <span className="supplier-info-label">✉️ Email</span>
                    <span className="supplier-info-value">
                      {supplierInfo.email}
                    </span>
                  </div>
                )}
                {supplierInfo.address && (
                  <div className="supplier-info-item">
                    <span className="supplier-info-label">📍 Address</span>
                    <span className="supplier-info-value">
                      {supplierInfo.address}
                    </span>
                  </div>
                )}
                {supplierInfo.website && (
                  <div className="supplier-info-item">
                    <span className="supplier-info-label">🌐 Website</span>
                    <span className="supplier-info-value">
                      {supplierInfo.website}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Products ── */}
        <div className="wh-section">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <p className="wh-section-title" style={{ margin: 0 }}>
              📦 Products Received
              {poStatus === "pending" && (
                <span
                  style={{
                    color: "var(--amber)",
                    fontSize: "11px",
                    marginLeft: "8px",
                  }}
                >
                  (will NOT be added to warehouse)
                </span>
              )}
              {poStatus === "cancelled" && (
                <span
                  style={{
                    color: "var(--red)",
                    fontSize: "11px",
                    marginLeft: "8px",
                  }}
                >
                  (will NOT be added to warehouse)
                </span>
              )}
              {poStatus === "partial" && (
                <span
                  style={{
                    color: "var(--purple)",
                    fontSize: "11px",
                    marginLeft: "8px",
                  }}
                >
                  (partial quantities will be added)
                </span>
              )}
              {poStatus === "completed" && (
                <span
                  style={{
                    color: "var(--green)",
                    fontSize: "11px",
                    marginLeft: "8px",
                  }}
                >
                  (all items will be added to warehouse)
                </span>
              )}
              <span
                style={{
                  color: "var(--text3)",
                  fontSize: "11px",
                  marginLeft: "8px",
                }}
              ></span>
            </p>
            <button className="wh-add-item-btn" onClick={addRow}>
              ＋ Add Row
            </button>
          </div>

          {/* Table Header — hidden on mobile; each row carries its own field labels there instead */}
          <div className="wh-received-header">
            <span>Product</span>
            <span style={{ textAlign: "center" }}>Qty (Purchase)</span>
            <span style={{ textAlign: "center" }}>Purchase UoM</span>
            <span style={{ textAlign: "right" }}>
              Unit Cost / Purchase (ETB)
            </span>
            <span>Expiry Date</span>
            <span>Batch No.</span>
            <span style={{ textAlign: "right" }}>Subtotal</span>
            <span></span>
          </div>

          {/* Table Rows */}
          {items.map((item, i) => (
            <ProductRow
              key={i}
              index={i}
              item={item}
              products={products}
              onUpdate={updateRow}
              onSelect={selectProduct}
              onRemove={removeRow}
            />
          ))}

          {/* Total */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 10px",
              borderTop: "2px solid var(--border2)",
              marginTop: "2px",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                color: "var(--text3)",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
              }}
            >
              Total Cost ({items.filter((i) => i.product_id).length} products)
            </span>
            <strong
              style={{
                fontSize: "18px",
                color: "var(--cyan)",
                fontFamily: "var(--mono)",
              }}
            >
              ETB {totalCost.toFixed(2)}
            </strong>
          </div>
        </div>

        {/* ── Submit ── */}
        <button
          className="inv-save-btn"
          onClick={handleReceive}
          disabled={saving}
          style={{
            background:
              poStatus === "cancelled"
                ? "var(--red)"
                : poStatus === "pending"
                  ? "var(--amber)"
                  : poStatus === "partial"
                    ? "var(--purple)"
                    : "var(--cyan)",
            color:
              poStatus === "pending"
                ? "#000"
                : poStatus === "cancelled"
                  ? "#fff"
                  : "#000",
            boxShadow: "none",
          }}
        >
          {saving
            ? "Saving..."
            : poStatus === "cancelled"
              ? "❌ Save as Cancelled (no stock added)"
              : poStatus === "pending"
                ? "⏳ Save as Pending (no stock added)"
                : poStatus === "partial"
                  ? "📦 Save Partial Delivery"
                  : "✅ Receive Stock into Warehouse"}
        </button>
      </div>
    </div>
  );
}

// ── Product Row Component ─────────────────────────────
function ProductRow({ index, item, products, onUpdate, onSelect, onRemove }) {
  const [search, setSearch] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const wrapRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowDrop(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = products
    .filter((p) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        (p.sku_code ?? "").toLowerCase().includes(q) ||
        (p.barcode ?? "").includes(q)
      );
    })
    .slice(0, 8);

  const selectedProduct = products.find((p) => p.id === item.product_id);
  const unitsPerPurchase = Math.max(
    1,
    Number(selectedProduct?.units_per_purchase ?? 1),
  );
  const saleUnitsToAdd = (Number(item.quantity) || 0) * unitsPerPurchase;

  const cellStyle = {
    height: "36px",
    padding: "0 10px",
    background: "var(--elevated)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    color: "var(--text)",
    fontFamily: "var(--mono)",
    fontSize: "13px",
    outline: "none",
    width: "100%",
    transition: "border 0.15s",
  };

  return (
    <div className="wh-received-row">
      {/* Product Search */}
      <div className="wh-received-field">
        <span className="wh-received-field-label">Product</span>
        <div ref={wrapRef} style={{ position: "relative" }}>
        <input
          style={{
            ...cellStyle,
            fontFamily: "var(--font)",
            color: selectedProduct ? "var(--text)" : "var(--text3)",
          }}
          placeholder="Type to search product..."
          value={selectedProduct ? selectedProduct.name : search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowDrop(true);
            if (item.product_id) onUpdate(index, "product_id", "");
          }}
          onFocus={() => {
            if (selectedProduct) {
              onUpdate(index, "product_id", "");
              setSearch("");
            }
            setShowDrop(true);
          }}
        />
        {showDrop && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              background: "var(--elevated)",
              border: "1px solid var(--cyan)",
              borderRadius: "var(--radius)",
              zIndex: 100,
              boxShadow: "var(--shadow-lg)",
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: "12px",
                  color: "var(--text3)",
                }}
              >
                No products found
              </div>
            ) : (
              filtered.map((p) => (
                <div
                  key={p.id}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--border)",
                    fontSize: "12px",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--cyan-dim)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(index, p);
                    setSearch("");
                    setShowDrop(false);
                  }}
                >
                  <span style={{ fontWeight: 600, color: "var(--text)" }}>
                    {p.name}
                  </span>
                  {p.sku_code && (
                    <span
                      style={{
                        color: "var(--cyan)",
                        marginLeft: "8px",
                        fontFamily: "var(--mono)",
                        fontSize: "11px",
                      }}
                    >
                      [{p.sku_code}]
                    </span>
                  )}
                  {p.barcode && (
                    <span
                      style={{
                        color: "var(--text3)",
                        marginLeft: "6px",
                        fontSize: "11px",
                      }}
                    >
                      {p.barcode}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
        </div>
      </div>

      {/* Qty */}
      <div className="wh-received-field">
        <span className="wh-received-field-label">Qty (Purchase)</span>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <input
          type="number"
          min="0"
          placeholder="0"
          value={item.quantity}
          onChange={(e) => onUpdate(index, "quantity", e.target.value)}
          style={{ ...cellStyle, textAlign: "center" }}
        />
        {!!selectedProduct && (Number(item.quantity) || 0) > 0 && (
          <div
            style={{
              fontSize: "10px",
              color: "var(--text3)",
              textAlign: "center",
              lineHeight: 1.1,
            }}
          >
            Adds{" "}
            <span style={{ color: "var(--amber)", fontFamily: "var(--mono)" }}>
              {saleUnitsToAdd}
            </span>{" "}
            sale units
            {unitsPerPurchase > 1 && (
              <span style={{ marginLeft: "4px", fontFamily: "var(--mono)" }}>
                ({unitsPerPurchase}×)
              </span>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Purchase UoM */}
      <div className="wh-received-field">
        <span className="wh-received-field-label">Purchase UoM</span>
        <input
          type="text"
          placeholder="purchase unit"
          value={item.uom}
          onChange={(e) => onUpdate(index, "uom", e.target.value)}
          style={{ ...cellStyle, textAlign: "center", fontFamily: "var(--font)" }}
        />
      </div>

      {/* Unit Cost per purchase unit */}
      <div className="wh-received-field">
        <span className="wh-received-field-label">Unit Cost / Purchase (ETB)</span>
        <input
          type="number"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={item.unit_cost}
          onChange={(e) => onUpdate(index, "unit_cost", e.target.value)}
          style={{ ...cellStyle, textAlign: "right", paddingRight: "12px" }}
        />
      </div>

      {/* Expiry Date */}
      <div className="wh-received-field">
        <span className="wh-received-field-label">Expiry Date</span>
        <input
          type="date"
          value={item.expiry_date}
          onChange={(e) => onUpdate(index, "expiry_date", e.target.value)}
          style={{ ...cellStyle, fontFamily: "var(--font)", fontSize: "12px" }}
        />
      </div>

      {/* Batch No */}
      <div className="wh-received-field">
        <span className="wh-received-field-label">Batch No.</span>
        <input
          type="text"
          placeholder="B-001"
          value={item.batch_number}
          onChange={(e) => onUpdate(index, "batch_number", e.target.value)}
          style={{ ...cellStyle, fontFamily: "var(--font)", fontSize: "12px" }}
        />
      </div>

      {/* Subtotal */}
      <div className="wh-received-field">
        <span className="wh-received-field-label">Subtotal</span>
        <div
          style={{
            textAlign: "right",
            fontFamily: "var(--mono)",
            fontSize: "13px",
            fontWeight: 600,
            color: "var(--cyan)",
          }}
        >
          ETB{" "}
          {(
            Number(item.quantity) * Number(item.unit_cost) || 0
          ).toFixed(2)}
        </div>
      </div>

      {/* Remove */}
      <button
        className="wh-received-remove"
        onClick={() => onRemove(index)}
        style={{
          width: "28px",
          height: "28px",
          background: "transparent",
          border: "1px solid var(--border)",
          borderRadius: "7px",
          color: "var(--text3)",
          cursor: "pointer",
          fontSize: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "var(--red)";
          e.currentTarget.style.color = "var(--red)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--border)";
          e.currentTarget.style.color = "var(--text3)";
        }}
      >
        <span className="wh-received-remove-icon">✕</span>
      </button>
    </div>
  );
}

// ══════════════════════════════════════
// TAB 2 — Suppliers
// ══════════════════════════════════════
function Suppliers({ user }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    tin: "",
    website: "",
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  async function fetchSuppliers() {
    const { data, error } = await supabase.from("suppliers").select("*").order("name");
    logSupabaseError("Warehouse: Suppliers fetch suppliers", error);
    if (data) setSuppliers(data);
    setLoading(false);
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function startEdit(s) {
    setEditing(s.id);
    setForm({
      name: s.name,
      phone: s.phone ?? "",
      email: s.email ?? "",
      address: s.address ?? "",
      tin: s.tin ?? "",
      website: s.website ?? "",
    });
  }

  function cancelEdit() {
    setEditing(null);
    setForm({
      name: "",
      phone: "",
      email: "",
      address: "",
      tin: "",
      website: "",
    });
  }

  async function handleSave() {
    if (!form.name) {
      alert("Supplier name is required!");
      return;
    }
    setSaving(true);

    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("store_id")
      .eq("id", user.id)
      .single();
    logSupabaseError("Warehouse: Suppliers load profile store_id", profileError);

    if (editing) {
      const { error } = await supabase.from("suppliers").update(form).eq("id", editing);
      if (error) {
        alert("❌ Failed to update supplier: " + error.message);
        setSaving(false);
        return;
      }
    } else {
      const { error } = await supabase.from("suppliers").insert({
        ...form,
        store_id: profileData?.store_id,
      });
      if (error) {
        alert("❌ Failed to save supplier: " + error.message);
        setSaving(false);
        return;
      }
    }
    setSaving(false);
    cancelEdit();
    fetchSuppliers();
  }

  async function handleDelete(s) {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    const { error } = await supabase.from("suppliers").delete().eq("id", s.id);
    if (error) {
      alert("❌ Failed to delete supplier: " + error.message);
      return;
    }
    fetchSuppliers();
  }

  const inputStyle = {
    height: "36px",
    padding: "0 12px",
    background: "var(--elevated)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    color: "var(--text)",
    fontFamily: "var(--font)",
    outline: "none",
    fontSize: "13px",
    width: "100%",
  };

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* Form */}
      <div className="wh-section">
        <p className="wh-section-title">
          {editing ? "✏️ Edit Supplier" : "➕ Add Supplier"}
        </p>
        <div
          className="wh-supplier-form-row"
          style={{ marginBottom: "12px" }}
        >
          {[
            {
              name: "name",
              label: "Supplier Name *",
              placeholder: "e.g. Addis Beverages",
            },
            {
              name: "tin",
              label: "TIN Number",
              placeholder: "e.g. 0012345678",
            },
            { name: "phone", label: "Phone", placeholder: "+251 9xx xxx xxx" },
            {
              name: "email",
              label: "Email",
              placeholder: "supplier@email.com",
            },
            { name: "address", label: "Address", placeholder: "City, Area" },
            {
              name: "website",
              label: "Website",
              placeholder: "www.supplier.com",
            },
          ].map((f) => (
            <div key={f.name} className="inv-form-field">
              <label>{f.label}</label>
              <input
                name={f.name}
                value={form[f.name]}
                onChange={handleChange}
                placeholder={f.placeholder}
                style={inputStyle}
              />
            </div>
          ))}
        </div>
        <div className="form-actions">
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editing ? "💾 Update" : "➕ Add Supplier"}
          </button>
          {editing && (
            <button className="cancel-btn" onClick={cancelEdit}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="wh-section">
        <p className="wh-section-title">🏢 Suppliers ({suppliers.length})</p>
        {loading ? (
          <p className="report-loading">Loading...</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {suppliers.map((s) => (
              <div key={s.id} className="wh-supplier-card">
                <div className="wh-supplier-info">
                  <p className="wh-supplier-name">{s.name}</p>
                  <div className="wh-supplier-details">
                    {s.tin && <span>🏛️ TIN: {s.tin}</span>}
                    {s.phone && <span>📞 {s.phone}</span>}
                    {s.email && <span>✉️ {s.email}</span>}
                    {s.address && <span>📍 {s.address}</span>}
                    {s.website && <span>🌐 {s.website}</span>}
                  </div>
                </div>
                <div className="action-btns">
                  <button className="edit-btn" onClick={() => startEdit(s)}>
                    ✏️
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(s)}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
            {suppliers.length === 0 && (
              <p style={{ color: "var(--text3)", fontSize: "13px" }}>
                No suppliers yet
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// TAB 3 — History
// ══════════════════════════════════════
function History() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    const { data, error } = await supabase
      .from("purchase_orders")
      .select(
        `
        id, total_cost, notes, created_at, status,
        suppliers(name),
        receiver:profiles!purchase_orders_received_by_fkey(full_name),
        purchase_order_items(
          quantity, unit_cost, line_total, expiry_date, batch_number,
          products(name, sku_code)
        )
      `,
      )
      .order("created_at", { ascending: false })
      .limit(50);
    logSupabaseError("Warehouse: History fetch purchase_orders", error);
    if (data) setOrders(data);
    setLoading(false);
  }

  const filtered =
    filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const statusColors = {
    completed: { color: "var(--green)", bg: "var(--green-dim)" },
    partial: { color: "var(--purple)", bg: "rgba(191,90,242,0.12)" },
    pending: { color: "var(--amber)", bg: "var(--amber-dim)" },
    cancelled: { color: "var(--red)", bg: "var(--red-dim)" },
    received: { color: "var(--cyan)", bg: "var(--cyan-dim)" },
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      {/* Filter tabs */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          padding: "12px 20px",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
        }}
      >
        {["all", "completed", "partial", "pending", "cancelled"].map((s) => (
          <button
            key={s}
            className={`filter-tab ${filter === s ? "active" : ""}`}
            onClick={() => setFilter(s)}
            style={{ textTransform: "capitalize" }}
          >
            {s === "all" ? "All Orders" : s}
            {s !== "all" && (
              <span style={{ marginLeft: "4px", fontSize: "10px" }}>
                ({orders.filter((o) => o.status === s).length})
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="report-table-wrap">
        {loading ? (
          <p className="report-loading">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="report-empty">
            <p>📋</p>
            <p>No orders found</p>
          </div>
        ) : (
          <table className="report-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Products</th>
                <th>Total Cost</th>
                <th>Received By</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const sc = statusColors[o.status] ?? statusColors.received;
                return (
                  <tr key={o.id}>
                    <td style={{ fontWeight: 600 }}>
                      {o.suppliers?.name ?? "—"}
                    </td>
                    <td style={{ fontSize: "12px", color: "var(--text2)" }}>
                      {o.purchase_order_items
                        ?.map(
                          (i) => `${i.products?.name ?? "?"} ×${i.quantity}`,
                        )
                        .join(", ") || "—"}
                    </td>
                    <td className="amount-col">
                      ETB {Number(o.total_cost).toFixed(2)}
                    </td>
                    <td style={{ color: "var(--text2)" }}>
                      {o.receiver?.full_name ?? "—"}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: "20px",
                          fontSize: "11px",
                          fontWeight: 700,
                          textTransform: "capitalize",
                          background: sc.bg,
                          color: sc.color,
                        }}
                      >
                        {o.status}
                      </span>
                    </td>
                    <td className="time-col">
                      {new Date(o.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
